/** Shipments, the shipment drawer, booking a parcel and shipping settings. Conventions: see `common.ts`. */
const shipping: Record<string, string> = {
  'status.PENDING': 'Pending',
  'status.FAILED': 'Booking failed',
  'status.CREATED': 'Booked',
  'status.PICKUP_SCHEDULED': 'Pickup scheduled',
  'status.PICKED_UP': 'Picked up',
  'status.IN_TRANSIT': 'In transit',
  'status.OUT_FOR_DELIVERY': 'Out for delivery',
  'status.DELIVERED': 'Delivered',
  'status.DELIVERY_FAILED': 'Delivery failed',
  'status.RTO': 'Returning to origin',
  'status.RTO_DELIVERED': 'Returned to origin',
  'status.RETURNED': 'Returned',
  'status.LOST': 'Lost',
  'status.CANCELLED': 'Cancelled',

  'kind.RETURN': 'Return',

  'errors.generic': 'Something went wrong. Try again.',

  'actions.booking': 'Booking…',
  'actions.retry_booking': 'Retry booking',
  'actions.checking': 'Checking…',
  'actions.keep_it': 'Keep it',

  'fields.courier': 'Courier',
  'fields.tracking_number': 'Tracking number',
  'fields.qty': 'Qty',
  'fields.note': 'Note',

  'segments.aria_label': 'Shipment views',
  'segments.attention': 'Needs attention',
  'segments.booked': 'Booked',
  'segments.transit': 'In transit',
  'segments.delivered': 'Delivered',
  'segments.returns': 'Returns',
  'segments.closed': 'Cancelled / lost',

  'list.title': 'Shipments',
  'list.in_view': '{total} in this view',
  'list.loading': 'Loading your shipments…',
  'list.settings_link': 'Shipping settings →',
  'list.search_label': 'Search shipments',
  'list.search_placeholder': 'Order number, tracking number, email or courier',
  'list.sort': 'Sort',
  'list.sort_newest': 'Newest first',
  'list.sort_oldest': 'Oldest first',
  'list.empty.search_title': 'No shipments match that search',
  'list.empty.search_description': 'Try an order number, a tracking number or the courier name.',
  'list.empty.all_title': 'Your shipments will show here',
  'list.empty.all_description': 'Book a shipment from an order and it will appear here with live tracking.',
  'list.empty.view_title': 'Nothing in this view',
  'list.empty.view_description': 'No shipments are in these statuses right now. Try another tab.',
  'list.columns.order': 'Order',
  'list.columns.customer': 'Customer',
  'list.columns.courier': 'Courier',
  'list.columns.status': 'Status',
  'list.columns.expected': 'Expected',
  'list.columns.booked': 'Booked',
  'list.columns.error': 'Error',
  'list.by_hand': 'By hand',

  'drawer.source.MERCHANT': 'You',
  'drawer.source.WEBHOOK': 'Courier',
  'drawer.source.SYNC': 'Tracking check',
  'drawer.source.SYSTEM': 'System',
  'drawer.notice.retry_failed': 'The courier refused the booking again.',
  'drawer.notice.booked': 'Booked with the courier.',
  'drawer.notice.cancelled': 'Shipment cancelled.',
  'drawer.notice.synced': 'Tracking refreshed from the courier.',
  'drawer.notice.pickup_requested': 'Pickup requested.',
  'drawer.notice.document_ready.label': 'The label is ready below.',
  'drawer.notice.document_ready.invoice': 'The invoice is ready below.',
  'drawer.notice.document_ready.manifest': 'The manifest is ready below.',
  'drawer.notice.marked': 'Marked {status}.',
  'drawer.title.shipment': 'Shipment for {order}',
  'drawer.title.return': 'Return for {order}',
  'drawer.title.order_fallback': 'order',
  'drawer.title.loading': 'Shipment',
  'drawer.loading': 'Loading shipment…',
  'drawer.load_failed': 'This shipment could not be loaded.',
  'drawer.recorded_by_hand': 'Recorded by hand',
  'drawer.courier_says': 'Courier says: {status}',
  'drawer.open_order': 'Open order →',
  'drawer.last_error': 'Last error:',
  'drawer.after_attempts': '(after {attempts} attempts)',
  'drawer.fields.courier_code': 'Courier code',
  'drawer.fields.provider_order': 'Provider order',
  'drawer.fields.provider_shipment': 'Provider shipment',
  'drawer.fields.pickup_reference': 'Pickup reference',
  'drawer.fields.courier_charge': 'Courier charge',
  'drawer.fields.cash_to_collect': 'Cash to collect',
  'drawer.fields.parcel': 'Parcel',
  'drawer.fields.booked': 'Booked',
  'drawer.fields.pickup': 'Pickup',
  'drawer.fields.expected': 'Expected',
  'drawer.fields.shipped': 'Shipped',
  'drawer.fields.delivered': 'Delivered',
  'drawer.fields.cancelled': 'Cancelled',
  'drawer.fields.last_tracked': 'Last tracked',
  'drawer.parcel_weight': '{grams} g',
  'drawer.parcel_dimensions': '{length} × {breadth} × {height} cm',
  'drawer.items.title': 'Items',
  'drawer.items.everything': 'Everything on the order.',
  'drawer.items.product': 'Product',
  'drawer.timeline.title': 'Timeline',
  'drawer.timeline.empty': 'Nothing has happened yet.',
  'drawer.documents.title': 'Documents',
  'drawer.documents.open.label': 'Open label',
  'drawer.documents.open.invoice': 'Open invoice',
  'drawer.documents.open.manifest': 'Open manifest',
  'drawer.documents.generate.label': 'Generate label',
  'drawer.documents.generate.invoice': 'Generate invoice',
  'drawer.documents.generate.manifest': 'Generate manifest',
  'drawer.documents.generating': 'Generating…',
  'drawer.actions.track_now': 'Track now',
  'drawer.actions.requesting': 'Requesting…',
  'drawer.actions.request_pickup': 'Request pickup',
  'drawer.actions.cancel_shipment': 'Cancel shipment',
  'drawer.cancel.prompt':
    'Cancel this shipment with the courier? The order stays as it is; you can book again afterwards.',
  'drawer.cancel.note_placeholder': 'Optional - why it was cancelled',
  'drawer.cancel.cancelling': 'Cancelling…',
  'drawer.cancel.confirm': 'Yes, cancel it',
  'drawer.update.label': 'Update status',
  'drawer.update.choose': 'Choose a status…',
  'drawer.update.hint': "For parcels you track yourself, or when the courier's feed is behind.",
  'drawer.update.submit': 'Update',

  'dialog.errors.create_failed': 'Could not create the shipment. Try again.',
  'dialog.rate.recommended': 'Recommended',
  'dialog.rate.days_one': '{count} day',
  'dialog.rate.days_other': '{count} days',
  'dialog.rate.no_eta': 'No ETA',
  'dialog.rate.by': 'by {date}',
  'dialog.rate.rated': 'rated {rating}',
  'dialog.rate.cod_charge': 'COD charge {amount}',
  'dialog.title': 'Ship {order}',
  'dialog.retrying': 'Retrying…',
  'dialog.record': 'Record shipment',
  'dialog.book': 'Book shipment',
  'dialog.failed.title': 'The courier refused the booking.',
  'dialog.failed.no_reason': 'No reason was given.',
  'dialog.failed.kept': 'The shipment has been kept so you can retry once the problem is fixed.',
  'dialog.mode.legend': 'How is it going out?',
  'dialog.mode.courier': 'Book with courier',
  'dialog.mode.courier_description': 'Get live rates and book the pickup through your connected provider.',
  'dialog.mode.manual': 'Record manually',
  'dialog.mode.manual_description': 'The parcel went out another way. Save the courier and tracking details.',
  'dialog.rates.loading': 'Asking the courier for rates…',
  'dialog.rates.none': 'No courier serves this address right now.',
  'dialog.rates.note': 'Courier prices. The shopper paid {amount} for delivery.',
  'dialog.weight': 'Parcel weight (grams)',
  'dialog.weight_placeholder': 'Leave blank to use your default',
  'dialog.weight_error': 'Whole grams only.',
  'dialog.request_pickup': 'Request pickup straight away',
  'dialog.manual.courier_placeholder': 'e.g. India Post, Dunzo',
  'dialog.manual.tracking_link': 'Tracking link',
  'dialog.manual.tracking_link_placeholder': 'https://… (optional, shown to the shopper)',
  'dialog.items.title': 'What is in this parcel',
  'dialog.items.all_shipped': 'Everything on this order has already shipped.',
  'dialog.items.include': 'Include {product}',
  'dialog.items.quantity_of': 'Quantity of {product}',
  'dialog.items.of_remaining': 'of {remaining}',

  'provider.open': 'Open {name}',
  'provider.badge.unavailable': 'Unavailable',
  'provider.badge.connected': 'Connected',
  'provider.badge.not_connected': 'Not connected',
  'provider.badge.paused': 'Paused',
  'provider.unavailable_reason': 'This courier is not available right now.',
  'provider.account_connected': 'Account connected',
  'provider.last_checked': 'last checked {date}',
  'provider.not_checked': 'not checked yet',
  'provider.pickup_location': 'Pickup location {code} registered with the courier.',
  'provider.last_said': '{name} last said: {error}',
  'provider.no_webhook_secret': 'No webhook secret set. Tracking updates will only arrive when you press Track now.',
  'provider.test_connection': 'Test connection',
  'provider.pause': 'Pause',
  'provider.resume': 'Resume',
  'provider.disconnect': 'Disconnect',
  'provider.disconnecting': 'Disconnecting…',
  'provider.replace_credentials': 'Replace your credentials',
  'provider.connect_title': 'Connect {name}',
  'provider.keep_secret_hint': 'Leave blank to keep the one you already saved.',
  'provider.checking_with': 'Checking with {name}…',
  'provider.save_and_connect': 'Save and connect',
  'provider.webhook.title': 'Webhook',
  'provider.webhook.intro': 'In {name}, add this URL as a webhook so tracking updates reach you as they happen.',
  'provider.webhook.header_hint': 'Send your webhook secret in the {header} header.',
  'provider.webhook.header_name': 'Header name:',
  'provider.disconnect_dialog.title': 'Disconnect {name}?',
  'provider.disconnect_dialog.body':
    'Your credentials are deleted and new shipments are recorded by hand. Parcels already with the courier keep moving, but their tracking stops updating here.',
  'provider.notice.connected': '{name} connected. You can book shipments from any order.',
  'provider.notice.verified': 'Connection is working.',
  'provider.notice.resumed': '{name} resumed.',
  'provider.notice.paused': '{name} paused. Shoppers see your flat or free delivery until you resume.',
  'provider.notice.disconnected': '{name} disconnected. Shipments are now recorded by hand.',

  'settings.title': 'Shipping',
  'settings.description': 'What delivery costs your shoppers, who carries the parcel, and where it is collected from.',
  'settings.saved': 'Shipping settings saved.',
  'settings.loading': 'Loading your shipping settings…',
  'settings.load_failed': 'Shipping settings could not be loaded.',
  'settings.no_business_address': 'No business address is saved yet. Add one under Settings.',
  'settings.errors.money': 'Enter an amount of 0 or more, with up to two decimals.',
  'settings.errors.integer_range': 'Enter a whole number from {min} to {max}.',
  'settings.errors.pickup_name': 'Who the courier asks for.',
  'settings.errors.pickup_line1': 'Enter the street address.',
  'settings.errors.pickup_city': 'Enter the city.',
  'settings.errors.pickup_state': 'Enter the state.',
  'settings.errors.pickup_postal_code': 'Enter a 6-digit PIN code.',
  'settings.errors.pickup_phone': 'Enter a phone number the courier can call.',
  'settings.enabled.on': 'Shipping is on',
  'settings.enabled.off': 'Shipping is off',
  'settings.enabled.on_description': 'Shoppers are quoted delivery at checkout by the rules below.',
  'settings.enabled.off_description': 'Delivery is not offered or charged. Orders still ship; nothing is quoted.',
  'settings.provider.label': 'Courier provider',
  'settings.provider.none': 'None - I record shipments by hand',
  'settings.provider.unavailable_option': '{name} (unavailable)',
  'settings.provider.hint':
    'Live rates and bookings need a connected provider. Without one, delivery is a flat or free charge and you record each shipment yourself.',
  'settings.integration.title': 'Courier integration',
  'settings.integration.not_included':
    'Booking shipments through a connected courier is not included in your plan. You can still record parcels you dispatch yourself.',
  'settings.integration.see_plans': 'See plans',
  'settings.pickup.title': 'Pickup address',
  'settings.pickup.description': 'Where the courier collects parcels from.',
  'settings.pickup.fetching': 'Fetching…',
  'settings.pickup.use_business_address': 'Use business address',
  'settings.pickup.registered_before': 'Registered with your courier as location',
  'settings.pickup.registered_after': '. Changing the address registers a new one on save.',
  'settings.pickup.contact_name': 'Contact name',
  'settings.pickup.phone': 'Phone',
  'settings.pickup.email': 'Email',
  'settings.pickup.line1': 'Address line 1',
  'settings.pickup.line2': 'Address line 2',
  'settings.pickup.city': 'City',
  'settings.pickup.state': 'State',
  'settings.pickup.postal_code': 'PIN code',
  'settings.pickup.country': 'Country',
  'settings.pickup.country_hint': 'Couriers here serve India.',
  'settings.charge.title': 'Delivery charge',
  'settings.charge.description': 'What a shopper pays for delivery at checkout.',
  'settings.charge.legend': 'Pricing',
  'settings.charge.provider': 'Courier rates',
  'settings.charge.provider_description': 'Quote the live rate for the shopper’s PIN code, plus any markup.',
  'settings.charge.markup': 'Markup',
  'settings.charge.markup_none': 'None - pass the courier rate on',
  'settings.charge.markup_percentage': 'Percentage on top',
  'settings.charge.markup_fixed': 'Fixed amount on top',
  'settings.charge.markup_percent_label': 'Markup %',
  'settings.charge.markup_amount_label': 'Markup amount',
  'settings.charge.handling_fee': 'Handling fee',
  'settings.charge.handling_fee_hint': 'Added to every quote for packing.',
  'settings.charge.round': 'Round the total',
  'settings.charge.round_none': 'Do not round',
  'settings.charge.round_rupee': 'To the nearest rupee',
  'settings.charge.round_five': 'Up to the next ₹5',
  'settings.charge.round_ten': 'Up to the next ₹10',
  'settings.charge.courier_selection': 'Which courier',
  'settings.charge.cheapest': 'Cheapest',
  'settings.charge.fastest': 'Fastest',
  'settings.charge.best_rated': 'Best rated',
  'settings.charge.shopper_choice': 'Let the shopper choose',
  'settings.charge.max_options': 'Options to show',
  'settings.charge.max_options_hint': 'Between 1 and 6.',
  'settings.charge.fallback': 'If the courier cannot quote',
  'settings.charge.fallback_flat': 'Charge a flat rate instead',
  'settings.charge.fallback_block': 'Do not accept the order',
  'settings.charge.fallback_rate': 'Fallback rate',
  'settings.charge.flat': 'Flat rate',
  'settings.charge.flat_description': 'The same charge on every order, wherever it goes.',
  'settings.charge.free': 'Free delivery',
  'settings.charge.free_description': 'Nothing is charged. You still book and pay the courier.',
  'settings.charge.free_above': 'Free delivery above',
  'settings.charge.free_above_placeholder': 'No threshold',
  'settings.charge.free_above_hint': 'Optional. Orders at or above this amount ship free.',
  'settings.charge.handling_days': 'Handling time (days)',
  'settings.charge.handling_days_hint': "Added to the courier's estimate so the date shown is honest.",
  'settings.cod.title': 'Cash on delivery',
  'settings.cod.description': 'Whether the courier collects cash, and when the option is shown.',
  'settings.cod.allow': 'Allow cash on delivery',
  'settings.cod.allow_hint': 'Also needs the option switched on under Checkout options.',
  'settings.cod.serviceable': 'Only where the courier can collect cash',
  'settings.cod.serviceable_hint': 'Hide cash on delivery for PIN codes the courier will not take cash to.',
  'settings.parcel.title': 'Default parcel',
  'settings.parcel.description':
    'Used to quote rates before anything is packed, and for any booking without its own weight.',
  'settings.parcel.weight': 'Weight (g)',
  'settings.parcel.length': 'Length (cm)',
  'settings.parcel.breadth': 'Breadth (cm)',
  'settings.parcel.height': 'Height (cm)',
  'settings.automation.title': 'Automation',
  'settings.automation.description': 'What happens on its own once an order is paid.',
  'settings.automation.auto_book': 'Book the shipment automatically',
  'settings.automation.auto_book_hint': 'When an order is confirmed, book it with the courier the shopper was quoted.',
  'settings.automation.auto_pickup': 'Request pickup automatically',
  'settings.automation.auto_pickup_hint': 'Ask the courier to collect as soon as the booking is made.',
  'settings.readiness.title': 'Readiness',
  'settings.readiness.ready': 'Shoppers can be quoted delivery at checkout.',
  'settings.readiness.not_ready': 'Not ready yet.',
  'settings.fix_fields': 'Fix the highlighted fields to save.',
  'settings.read_only': 'You have read-only access to store settings.',

  // >>> QA phase 5 additions
  'provider.credential.email.label': 'API user email',
  'provider.credential.email.help':
    'An API user created under Settings → API → Configure in your Shiprocket panel. Not your login email.',
  'provider.credential.password.label': 'API user password',
  'provider.credential.password.help': 'The password you gave that API user.',
  'provider.credential.webhook_secret.label': 'Webhook token',
  'provider.credential.webhook_secret.help':
    'Any long random string. Paste the same value into the webhook settings in Shiprocket as the x-api-key token so status updates can be trusted.',
};

export default shipping;
